import React, { type CSSProperties } from "react";
import {
  Award,
  BadgeCheck,
  Building2,
  CalendarDays,
  Check,
  Home,
  House,
  Leaf,
  Map,
  Minus,
  Phone,
  Shield,
  ShieldCheck,
  Tag,
  Truck,
  Wrench,
} from "lucide-react";
import {
  heroTrustIconTokens,
  heroTrustTokens,
  type HeroTrustColorToken,
  type HeroTrustIconSlot,
  type HeroTrustIconToken,
} from "./HeroTrust.tokens";

export type HeroTrustLinkComponent = React.ComponentType<{
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  target?: string;
  rel?: string;
  "aria-label"?: string;
  [key: string]: any;
}>;

export type HeroTrustImage = {
  src?: string;
  url?: string;
  original?: string;
  thumbnail?: string;
  alt?: string;
  title?: string;
  width?: number;
  height?: number;
  loading?: "eager" | "lazy";
  decorative?: boolean;
  position?: string;
};

export type HeroTrustIcon = HeroTrustIconToken | React.ReactNode;

export type HeroTrustFeature = {
  id?: string | number;
  icon?: HeroTrustIcon;
  title: React.ReactNode;
  text?: React.ReactNode;
};

export type HeroTrustProofItem = {
  id?: string | number;
  icon?: HeroTrustIcon;
  title: React.ReactNode;
  text?: React.ReactNode;
};

export type HeroTrustAction = {
  label: React.ReactNode;
  href: string;
  mobileLabel?: React.ReactNode;
  description?: React.ReactNode;
  ariaLabel?: string;
  title?: string;
  target?: string;
  rel?: string;
  icon?: HeroTrustIcon;
};

export type HeroTrustSupportNote = {
  title: React.ReactNode;
  text?: React.ReactNode;
  icon?: HeroTrustIcon;
};

export type HeroTrustColors = Partial<Record<HeroTrustColorToken, string>>;
export type HeroTrustIcons = Partial<Record<HeroTrustIconSlot, HeroTrustIconToken>>;
export type HeroTrustTextColor = "white" | "black" | string;

export type HeroTrustProps = {
  Link?: HeroTrustLinkComponent;
  image?: HeroTrustImage;
  eyebrow?: React.ReactNode;
  title?: string;
  titleHighlights?: string[];
  titleComponent?: "h1" | "h2" | "h3";
  lead?: React.ReactNode;
  completeTitle?: React.ReactNode;
  completeText?: React.ReactNode;
  reassuranceTitle?: React.ReactNode;
  reassuranceText?: React.ReactNode;
  primaryFeatures?: HeroTrustFeature[];
  secondaryFeatures?: HeroTrustFeature[];
  mobileFeatures?: HeroTrustFeature[];
  primaryAction?: HeroTrustAction | false;
  secondaryAction?: HeroTrustAction | false;
  supportNote?: HeroTrustSupportNote | false;
  proofItems?: HeroTrustProofItem[];
  icons?: HeroTrustIcons;
  colors?: HeroTrustColors;
  colours?: HeroTrustColors;
  accentColor?: string;
  accentColour?: string;
  backgroundColor?: string;
  backgroundColour?: string;
  panelColor?: string;
  panelColour?: string;
  textColor?: HeroTrustTextColor;
  textColour?: HeroTrustTextColor;
  surfaceColor?: string;
  surfaceColour?: string;
  surfaceTextColor?: HeroTrustTextColor;
  surfaceTextColour?: HeroTrustTextColor;
  maxWidth?: number | string;
  id?: string;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
};

type CssVarStyle = CSSProperties &
  Record<`--hero-trust-${string}`, string | number | undefined>;

const t = heroTrustTokens;

const iconRegistry: Record<HeroTrustIconToken, React.ElementType> = {
  award: Award,
  badgeCheck: BadgeCheck,
  building: Building2,
  calendar: CalendarDays,
  check: Check,
  home: Home,
  house: House,
  houseCheck: House,
  leaf: Leaf,
  map: Map,
  minus: Minus,
  phone: Phone,
  shield: Shield,
  shieldCheck: ShieldCheck,
  tag: Tag,
  truck: Truck,
  wrench: Wrench,
};

const heroTrustCss = `
.hero-trust {
  --hero-trust-bg: ${t.colors.background};
  --hero-trust-panel: ${t.colors.panel};
  --hero-trust-text: ${t.colors.text};
  --hero-trust-text-soft: ${t.colors.textSoft};
  --hero-trust-text-muted: ${t.colors.textMuted};
  --hero-trust-accent: ${t.colors.accent};
  --hero-trust-accent-dark: ${t.colors.accentDark};
  --hero-trust-accent-soft: ${t.colors.accentSoft};
  --hero-trust-accent-contrast: ${t.colors.accentContrast};
  --hero-trust-dark-surface: ${t.colors.darkSurface};
  --hero-trust-dark-surface-strong: ${t.colors.darkSurfaceStrong};
  --hero-trust-dark-border: ${t.colors.darkBorder};
  --hero-trust-light-surface: ${t.colors.lightSurface};
  --hero-trust-light-text: ${t.colors.lightText};
  --hero-trust-light-text-soft: ${t.colors.lightTextSoft};
  --hero-trust-light-border: ${t.colors.lightBorder};
  --hero-trust-proof-surface: ${t.colors.proofSurface};
  --hero-trust-proof-text: ${t.colors.proofText};
  --hero-trust-proof-text-soft: ${t.colors.proofTextSoft};
  --hero-trust-proof-divider: ${t.colors.proofDivider};
  --hero-trust-icon-ring: ${t.colors.iconRing};
  --hero-trust-light-icon-ring: ${t.colors.lightIconRing};
  --hero-trust-shadow: ${t.colors.shadow};
  --hero-trust-max-width: ${t.sizes.maxWidth}px;
  width: 100%;
  padding: 32px 24px;
  overflow: hidden;
  background: var(--hero-trust-bg);
  color: var(--hero-trust-text);
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
}
.hero-trust,
.hero-trust * {
  box-sizing: border-box;
}
.hero-trust-shell {
  width: 100%;
  max-width: var(--hero-trust-max-width);
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid var(--hero-trust-light-border);
  border-radius: ${t.radius.shell}px;
  background: var(--hero-trust-panel);
  box-shadow: var(--hero-trust-shadow);
}
.hero-trust-panel {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  min-height: ${t.sizes.desktopMinHeight}px;
  background:
    radial-gradient(circle at 64% 28%, color-mix(in srgb, var(--hero-trust-accent) 13%, transparent), transparent 26%),
    linear-gradient(135deg, var(--hero-trust-panel), color-mix(in srgb, var(--hero-trust-panel) 88%, #3f3b30));
}
.hero-trust-panel::before,
.hero-trust-panel::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
}
.hero-trust-panel::before {
  background:
    linear-gradient(90deg, var(--hero-trust-panel) 0%, color-mix(in srgb, var(--hero-trust-panel) 96%, transparent) 34%, color-mix(in srgb, var(--hero-trust-panel) 70%, transparent) 58%, color-mix(in srgb, var(--hero-trust-panel) 28%, transparent) 100%),
    linear-gradient(180deg, color-mix(in srgb, var(--hero-trust-panel) 56%, transparent) 0%, var(--hero-trust-panel) 100%);
}
.hero-trust-panel::after {
  background: linear-gradient(180deg, transparent 52%, color-mix(in srgb, var(--hero-trust-panel) 92%, transparent) 100%);
}
.hero-trust[data-has-image="false"] .hero-trust-panel::before {
  background:
    radial-gradient(circle at 78% 18%, color-mix(in srgb, var(--hero-trust-accent) 11%, transparent), transparent 24%),
    linear-gradient(135deg, color-mix(in srgb, var(--hero-trust-panel) 96%, #ffffff) 0%, var(--hero-trust-panel) 100%);
}
.hero-trust-image {
  position: absolute;
  z-index: -2;
  top: 0;
  right: 0;
  width: min(47%, 690px);
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: 0.92;
}
.hero-trust-content {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0;
  min-height: inherit;
  padding: 72px 70px 32px;
}
.hero-trust-header {
  max-width: 960px;
}
.hero-trust-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  max-width: 100%;
  color: var(--hero-trust-text);
  font-size: ${t.typography.eyebrow.fontSize}px;
  line-height: ${t.typography.eyebrow.lineHeight};
  font-weight: ${t.typography.eyebrow.fontWeight};
  letter-spacing: ${t.typography.eyebrow.letterSpacing};
  text-transform: uppercase;
}
.hero-trust-eyebrow-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: var(--hero-trust-accent);
}
.hero-trust-eyebrow-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.hero-trust-title {
  max-width: 920px;
  margin: 28px 0 0;
  color: var(--hero-trust-text);
  font-size: ${t.typography.title.fontSize}px;
  line-height: ${t.typography.title.lineHeight};
  font-weight: ${t.typography.title.fontWeight};
  letter-spacing: ${t.typography.title.letterSpacing};
}
.hero-trust-title-accent {
  color: var(--hero-trust-accent);
}
.hero-trust-lead {
  max-width: 560px;
  margin: 28px 0 0;
  color: var(--hero-trust-text-soft);
  font-size: ${t.typography.lead.fontSize}px;
  line-height: ${t.typography.lead.lineHeight};
  font-weight: ${t.typography.lead.fontWeight};
  letter-spacing: ${t.typography.lead.letterSpacing};
}
.hero-trust-feature-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.96fr) minmax(360px, 0.82fr);
  align-items: stretch;
  gap: 18px;
  width: min(100%, 1120px);
  margin-top: 48px;
}
.hero-trust-feature-card {
  min-width: 0;
  border-radius: ${t.radius.card}px;
}
.hero-trust-feature-card-dark {
  padding: 28px 30px;
  border: 1px solid var(--hero-trust-dark-border);
  background: var(--hero-trust-dark-surface);
  color: var(--hero-trust-text);
  backdrop-filter: blur(12px);
}
.hero-trust-feature-card-light {
  padding: 28px 30px;
  border: 1px solid var(--hero-trust-light-border);
  background: var(--hero-trust-light-surface);
  color: var(--hero-trust-light-text);
}
.hero-trust-complete-header,
.hero-trust-reassurance-header {
  display: none;
  align-items: center;
  gap: 18px;
  margin-bottom: 28px;
}
.hero-trust-card-badge {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: ${t.radius.circle}px;
  background: var(--hero-trust-accent);
  color: var(--hero-trust-accent-contrast);
}
.hero-trust-card-heading {
  display: grid;
  min-width: 0;
  gap: 4px;
}
.hero-trust-card-title {
  color: inherit;
  font-size: 22px;
  line-height: 1.18;
  font-weight: 900;
  letter-spacing: 0;
}
.hero-trust-card-text {
  color: currentColor;
  opacity: 0.74;
  font-size: 15px;
  line-height: 1.34;
  font-weight: 500;
  letter-spacing: 0;
}
.hero-trust-feature-list {
  display: grid;
  gap: 24px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.hero-trust-feature-item {
  display: grid;
  grid-template-columns: 58px 26px minmax(0, 1fr);
  align-items: center;
  gap: 18px;
  min-width: 0;
}
.hero-trust-feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  border: 1px solid var(--hero-trust-icon-ring);
  border-radius: ${t.radius.circle}px;
  color: var(--hero-trust-text);
}
.hero-trust-feature-card-light .hero-trust-feature-icon {
  border-color: var(--hero-trust-light-icon-ring);
  color: var(--hero-trust-light-text-soft);
}
.hero-trust-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: ${t.radius.circle}px;
  background: var(--hero-trust-accent);
  color: var(--hero-trust-accent-contrast);
}
.hero-trust-feature-copy {
  display: grid;
  min-width: 0;
  gap: 6px;
}
.hero-trust-feature-title {
  min-width: 0;
  overflow-wrap: anywhere;
  color: inherit;
  font-size: ${t.typography.featureTitle.fontSize}px;
  line-height: ${t.typography.featureTitle.lineHeight};
  font-weight: ${t.typography.featureTitle.fontWeight};
  letter-spacing: ${t.typography.featureTitle.letterSpacing};
}
.hero-trust-feature-text {
  min-width: 0;
  overflow-wrap: anywhere;
  color: currentColor;
  opacity: 0.78;
  font-size: ${t.typography.featureText.fontSize}px;
  line-height: ${t.typography.featureText.lineHeight};
  font-weight: ${t.typography.featureText.fontWeight};
  letter-spacing: ${t.typography.featureText.letterSpacing};
}
.hero-trust-mobile-features {
  display: none;
}
.hero-trust-actions-panel {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(220px, 282px) minmax(210px, 244px);
  align-items: center;
  gap: 22px;
  width: min(100%, 1148px);
  margin-top: 22px;
  padding: 20px 28px;
  border: 1px solid var(--hero-trust-dark-border);
  border-radius: ${t.radius.card}px;
  background: var(--hero-trust-dark-surface-strong);
  backdrop-filter: blur(12px);
}
.hero-trust-support-note {
  display: grid;
  grid-template-columns: 78px minmax(0, 1fr);
  align-items: center;
  gap: 24px;
  min-width: 0;
}
.hero-trust-support-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 78px;
  height: 78px;
  border-radius: ${t.radius.control}px;
  background: var(--hero-trust-accent);
  color: var(--hero-trust-accent-contrast);
}
.hero-trust-support-copy {
  display: grid;
  min-width: 0;
  gap: 6px;
}
.hero-trust-support-title {
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--hero-trust-text);
  font-size: 20px;
  line-height: 1.2;
  font-weight: 900;
  letter-spacing: 0;
}
.hero-trust-support-text {
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--hero-trust-text-soft);
  font-size: 15px;
  line-height: 1.4;
  font-weight: 500;
  letter-spacing: 0;
}
.hero-trust-action {
  display: inline-grid;
  grid-template-columns: auto minmax(0, auto);
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-width: 0;
  min-height: 66px;
  padding: 10px 24px;
  border-radius: ${t.radius.control}px;
  color: var(--hero-trust-text);
  font-size: ${t.typography.action.fontSize}px;
  line-height: ${t.typography.action.lineHeight};
  font-weight: ${t.typography.action.fontWeight};
  letter-spacing: ${t.typography.action.letterSpacing};
  text-align: center;
  text-decoration: none;
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
}
.hero-trust-action:hover,
.hero-trust-action:focus-visible {
  transform: translateY(-1px);
}
.hero-trust-action:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--hero-trust-accent) 28%, transparent);
  outline-offset: 3px;
}
.hero-trust-action-primary {
  border: 1px solid var(--hero-trust-accent);
  background: var(--hero-trust-accent);
  color: var(--hero-trust-accent-contrast);
  box-shadow: inset 0 -1px 0 color-mix(in srgb, #000000 14%, transparent);
}
.hero-trust-action-primary:hover,
.hero-trust-action-primary:focus-visible {
  border-color: var(--hero-trust-accent-dark);
  background: var(--hero-trust-accent-dark);
}
.hero-trust-action-secondary {
  border: 1px solid color-mix(in srgb, var(--hero-trust-text) 64%, transparent);
  background: color-mix(in srgb, var(--hero-trust-panel) 68%, transparent);
}
.hero-trust-action-secondary:hover,
.hero-trust-action-secondary:focus-visible {
  border-color: var(--hero-trust-text);
  background: color-mix(in srgb, var(--hero-trust-panel) 52%, transparent);
}
.hero-trust-action-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
}
.hero-trust-action-copy {
  display: grid;
  min-width: 0;
  gap: 4px;
  justify-items: start;
}
.hero-trust-action-primary .hero-trust-action-copy {
  justify-items: center;
}
.hero-trust-action-label,
.hero-trust-action-label-mobile {
  min-width: 0;
  overflow-wrap: anywhere;
  color: inherit;
}
.hero-trust-action-label-mobile {
  display: none;
}
.hero-trust-action-description {
  min-width: 0;
  overflow-wrap: anywhere;
  color: inherit;
  opacity: 0.78;
  font-size: ${t.typography.actionDescription.fontSize}px;
  line-height: ${t.typography.actionDescription.lineHeight};
  font-weight: ${t.typography.actionDescription.fontWeight};
  letter-spacing: ${t.typography.actionDescription.letterSpacing};
}
.hero-trust-action-primary .hero-trust-action-description {
  display: none;
}
.hero-trust-proof-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: center;
  gap: 0;
  min-height: 146px;
  padding: 30px 48px;
  border-top: 1px solid var(--hero-trust-light-border);
  background: var(--hero-trust-proof-surface);
  color: var(--hero-trust-proof-text);
}
.hero-trust-proof-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 18px;
  min-width: 0;
  min-height: 74px;
  padding: 0 34px;
  border-left: 1px solid var(--hero-trust-proof-divider);
}
.hero-trust-proof-item:first-child {
  border-left: 0;
}
.hero-trust-proof-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 74px;
  height: 74px;
  border: 1px solid color-mix(in srgb, var(--hero-trust-proof-text) 38%, transparent);
  border-radius: ${t.radius.circle}px;
  color: var(--hero-trust-proof-text);
}
.hero-trust-proof-copy {
  display: grid;
  min-width: 0;
  gap: 4px;
}
.hero-trust-proof-title {
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--hero-trust-proof-text);
  font-size: ${t.typography.proof.fontSize}px;
  line-height: ${t.typography.proof.lineHeight};
  font-weight: ${t.typography.proof.fontWeight};
  letter-spacing: ${t.typography.proof.letterSpacing};
  text-transform: uppercase;
}
.hero-trust-proof-text {
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--hero-trust-proof-text-soft);
  font-size: 12px;
  line-height: 1.25;
  font-weight: 650;
  letter-spacing: 0;
}
@media (max-width: 1180px) {
  .hero-trust {
    padding: 26px 24px;
  }
  .hero-trust-panel {
    min-height: 0;
  }
  .hero-trust-image {
    width: min(46%, 520px);
  }
  .hero-trust-content {
    padding: 62px 60px 20px;
  }
  .hero-trust-header {
    max-width: 720px;
  }
  .hero-trust-title {
    max-width: 650px;
    font-size: ${t.typography.title.tabletFontSize}px;
  }
  .hero-trust-lead {
    max-width: 560px;
    margin-top: 24px;
    font-size: ${t.typography.lead.tabletFontSize}px;
  }
  .hero-trust-feature-grid {
    grid-template-columns: minmax(0, 1fr);
    width: 100%;
    margin-top: 38px;
    gap: 16px;
  }
  .hero-trust-feature-card-dark,
  .hero-trust-feature-card-light {
    padding: 28px 30px;
  }
  .hero-trust-complete-header,
  .hero-trust-reassurance-header {
    display: flex;
  }
  .hero-trust-feature-card-dark .hero-trust-feature-list,
  .hero-trust-feature-card-light .hero-trust-feature-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 44px;
    row-gap: 28px;
  }
  .hero-trust-feature-card-dark .hero-trust-feature-list {
    padding-top: 0;
  }
  .hero-trust-feature-item {
    grid-template-columns: 26px minmax(0, 1fr);
    gap: 14px;
  }
  .hero-trust-feature-icon {
    display: none;
  }
  .hero-trust-check {
    grid-column: 1;
  }
  .hero-trust-actions-panel {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    width: 100%;
    margin-top: 18px;
    padding: 20px 42px;
    gap: 62px;
  }
  .hero-trust-support-note {
    display: none;
  }
  .hero-trust-action {
    min-height: 96px;
  }
  .hero-trust-action-primary .hero-trust-action-description {
    display: block;
  }
  .hero-trust-proof-strip {
    min-height: 112px;
    padding: 22px 28px;
  }
  .hero-trust-proof-item {
    gap: 16px;
    min-height: 68px;
    padding: 0 24px;
  }
  .hero-trust-proof-icon {
    width: 66px;
    height: 66px;
  }
}
@media (max-width: 760px) {
  .hero-trust {
    padding: 24px 14px;
  }
  .hero-trust-shell {
    max-width: 430px;
    border-color: transparent;
  }
  .hero-trust-panel {
    background: var(--hero-trust-panel);
  }
  .hero-trust-panel::before {
    background:
      linear-gradient(180deg, color-mix(in srgb, var(--hero-trust-panel) 76%, transparent) 0%, var(--hero-trust-panel) 30%, var(--hero-trust-panel) 100%),
      linear-gradient(90deg, var(--hero-trust-panel) 0%, color-mix(in srgb, var(--hero-trust-panel) 82%, transparent) 100%);
  }
  .hero-trust-image {
    top: 0;
    right: 0;
    width: 48%;
    height: 310px;
    opacity: 0.82;
  }
  .hero-trust-content {
    padding: 44px 28px 22px;
  }
  .hero-trust-eyebrow {
    gap: 8px;
    font-size: ${t.typography.eyebrow.mobileFontSize}px;
  }
  .hero-trust-title {
    max-width: 335px;
    margin-top: 24px;
    font-size: ${t.typography.title.mobileFontSize}px;
    line-height: 1.12;
  }
  .hero-trust-lead {
    max-width: 300px;
    margin-top: 20px;
    font-size: ${t.typography.lead.mobileFontSize}px;
    line-height: 1.48;
  }
  .hero-trust-feature-grid {
    display: none;
  }
  .hero-trust-mobile-features {
    display: grid;
    gap: 8px;
    margin: 26px 0 0;
    padding: 0;
    list-style: none;
  }
  .hero-trust-mobile-card {
    display: grid;
    grid-template-columns: 70px 26px minmax(0, 1fr);
    align-items: center;
    gap: 16px;
    min-width: 0;
    min-height: 90px;
    padding: 10px 18px;
    border: 1px solid var(--hero-trust-dark-border);
    border-radius: ${t.radius.card}px;
    background: var(--hero-trust-dark-surface);
    color: var(--hero-trust-text);
  }
  .hero-trust-mobile-card .hero-trust-feature-icon {
    display: inline-flex;
    width: 62px;
    height: 62px;
  }
  .hero-trust-mobile-card .hero-trust-check {
    grid-column: auto;
  }
  .hero-trust-mobile-card .hero-trust-feature-title {
    font-size: ${t.typography.featureTitle.mobileFontSize}px;
  }
  .hero-trust-mobile-card .hero-trust-feature-text {
    font-size: ${t.typography.featureText.mobileFontSize}px;
    line-height: 1.34;
  }
  .hero-trust-actions-panel {
    grid-template-columns: minmax(0, 1fr);
    margin-top: 10px;
    padding: 0;
    border: 0;
    background: transparent;
    gap: 10px;
  }
  .hero-trust-action {
    width: 100%;
    min-height: 68px;
    padding: 10px 18px;
    font-size: ${t.typography.action.mobileFontSize}px;
  }
  .hero-trust-action-label-mobile + .hero-trust-action-label {
    display: none;
  }
  .hero-trust-action-label-mobile {
    display: inline;
  }
  .hero-trust-action-description {
    font-size: ${t.typography.actionDescription.mobileFontSize}px;
  }
  .hero-trust-action-primary .hero-trust-action-description {
    display: none;
  }
  .hero-trust-action-secondary {
    min-height: 72px;
  }
  .hero-trust-proof-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    min-height: 0;
    margin: 0 28px 22px;
    padding: 16px 18px;
    border: 0;
    border-radius: ${t.radius.card}px;
  }
  .hero-trust-proof-item {
    grid-template-columns: 64px minmax(0, 1fr);
    gap: 12px;
    min-height: 72px;
    padding: 10px 8px;
    border-left: 0;
  }
  .hero-trust-proof-item:nth-child(2n) {
    border-left: 1px solid var(--hero-trust-proof-divider);
  }
  .hero-trust-proof-item:nth-child(n + 3) {
    border-top: 1px solid var(--hero-trust-proof-divider);
  }
  .hero-trust-proof-icon {
    width: 54px;
    height: 54px;
  }
  .hero-trust-proof-title {
    font-size: ${t.typography.proof.mobileFontSize}px;
    line-height: 1.18;
  }
}
@media (max-width: 390px) {
  .hero-trust-content {
    padding-left: 20px;
    padding-right: 20px;
  }
  .hero-trust-title {
    font-size: 33px;
  }
  .hero-trust-lead {
    font-size: 17px;
  }
  .hero-trust-mobile-card {
    grid-template-columns: 58px 24px minmax(0, 1fr);
    gap: 12px;
    padding-left: 12px;
    padding-right: 12px;
  }
  .hero-trust-mobile-card .hero-trust-feature-icon {
    width: 54px;
    height: 54px;
  }
  .hero-trust-mobile-card .hero-trust-feature-title {
    font-size: 17px;
  }
  .hero-trust-mobile-card .hero-trust-feature-text {
    font-size: 14px;
  }
  .hero-trust-proof-strip {
    margin-left: 20px;
    margin-right: 20px;
    padding-left: 10px;
    padding-right: 10px;
  }
  .hero-trust-proof-item {
    grid-template-columns: 48px minmax(0, 1fr);
    gap: 9px;
  }
  .hero-trust-proof-icon {
    width: 44px;
    height: 44px;
  }
  .hero-trust-proof-title {
    font-size: 10px;
  }
}
`;

function getCssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function sanitizeId(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "") || "hero-trust"
  );
}

function getImageSrc(image?: HeroTrustImage): string {
  if (!image) return "";
  return image.thumbnail || image.src || image.url || image.original || "";
}

function getImageAlt(image?: HeroTrustImage, fallback = ""): string {
  if (!image) return fallback;
  return image.alt || image.title || fallback;
}

function resolveTextColor(color?: HeroTrustTextColor): string | undefined {
  if (!color) return undefined;
  if (color === "white") return "#ffffff";
  if (color === "black") return "#000000";
  return color;
}

function getSoftTextColor(color?: string): string | undefined {
  if (!color) return undefined;
  if (color === "#000000" || color.toLowerCase() === "black") {
    return "rgba(0, 0, 0, 0.72)";
  }
  if (color === "#ffffff" || color.toLowerCase() === "white") {
    return "rgba(255, 255, 255, 0.78)";
  }
  return `color-mix(in srgb, ${color} 78%, transparent)`;
}

function renderHighlightedTitle(title: string, highlights: string[]) {
  const matches = highlights
    .flatMap((highlight) => {
      const ranges: Array<{ start: number; end: number }> = [];
      if (!highlight) return ranges;

      const lowerTitle = title.toLowerCase();
      const lowerHighlight = highlight.toLowerCase();
      let searchIndex = 0;

      while (searchIndex < title.length) {
        const index = lowerTitle.indexOf(lowerHighlight, searchIndex);
        if (index === -1) break;
        ranges.push({ start: index, end: index + highlight.length });
        searchIndex = index + highlight.length;
      }

      return ranges;
    })
    .sort((a, b) => a.start - b.start);

  if (!matches.length) return title;

  const parts: React.ReactNode[] = [];
  let cursor = 0;

  matches.forEach((match, index) => {
    if (match.start < cursor) return;

    if (match.start > cursor) {
      parts.push(title.slice(cursor, match.start));
    }

    parts.push(
      <span className="hero-trust-title-accent" key={`accent-${match.start}-${index}`}>
        {title.slice(match.start, match.end)}
      </span>
    );
    cursor = match.end;
  });

  if (cursor < title.length) {
    parts.push(title.slice(cursor));
  }

  return parts;
}

function TokenIcon({
  icon,
  className,
  size,
  strokeWidth = 2.1,
}: {
  icon?: HeroTrustIcon;
  className?: string;
  size: number;
  strokeWidth?: number;
}) {
  if (React.isValidElement(icon)) {
    return <span className={className}>{icon}</span>;
  }

  const token = (icon || "shieldCheck") as HeroTrustIconToken;
  const Icon = iconRegistry[token] || ShieldCheck;

  return (
    <Icon
      aria-hidden="true"
      focusable="false"
      className={className}
      size={size}
      strokeWidth={strokeWidth}
    />
  );
}

function FeatureList({
  features,
  icons,
  className = "",
}: {
  features: HeroTrustFeature[];
  icons: Required<HeroTrustIcons>;
  className?: string;
}) {
  return (
    <ul className={`hero-trust-feature-list ${className}`.trim()}>
      {features.map((feature, index) => (
        <li className="hero-trust-feature-item" key={feature.id || `${feature.title}-${index}`}>
          <span className="hero-trust-feature-icon" aria-hidden="true">
            <TokenIcon icon={feature.icon} size={31} strokeWidth={1.8} />
          </span>
          <span className="hero-trust-check" aria-hidden="true">
            <TokenIcon icon={icons.check} size={15} strokeWidth={3} />
          </span>
          <span className="hero-trust-feature-copy">
            <span className="hero-trust-feature-title">{feature.title}</span>
            {feature.text ? <span className="hero-trust-feature-text">{feature.text}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

function MobileFeatureList({
  features,
  icons,
}: {
  features: HeroTrustFeature[];
  icons: Required<HeroTrustIcons>;
}) {
  return (
    <ul className="hero-trust-mobile-features">
      {features.map((feature, index) => (
        <li className="hero-trust-mobile-card" key={feature.id || `mobile-${feature.title}-${index}`}>
          <span className="hero-trust-feature-icon" aria-hidden="true">
            <TokenIcon icon={feature.icon} size={31} strokeWidth={1.8} />
          </span>
          <span className="hero-trust-check" aria-hidden="true">
            <TokenIcon icon={icons.check} size={15} strokeWidth={3} />
          </span>
          <span className="hero-trust-feature-copy">
            <span className="hero-trust-feature-title">{feature.title}</span>
            {feature.text ? <span className="hero-trust-feature-text">{feature.text}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

function HeroAction({
  action,
  variant,
  Link,
}: {
  action: HeroTrustAction;
  variant: "primary" | "secondary";
  Link?: HeroTrustLinkComponent;
}) {
  const LinkComponent = (Link || "a") as React.ElementType;
  const rel = action.rel || (action.target === "_blank" ? "noopener noreferrer" : undefined);

  return (
    <LinkComponent
      href={action.href}
      className={`hero-trust-action hero-trust-action-${variant}`}
      aria-label={action.ariaLabel}
      title={action.title}
      target={action.target}
      rel={rel}
    >
      <span className="hero-trust-action-icon" aria-hidden="true">
        <TokenIcon icon={action.icon} size={variant === "primary" ? 26 : 30} strokeWidth={2.4} />
      </span>
      <span className="hero-trust-action-copy">
        {action.mobileLabel ? (
          <span className="hero-trust-action-label-mobile">{action.mobileLabel}</span>
        ) : null}
        <span className="hero-trust-action-label">{action.label}</span>
        {action.description ? (
          <span className="hero-trust-action-description">{action.description}</span>
        ) : null}
      </span>
    </LinkComponent>
  );
}

function ProofStrip({ items }: { items: HeroTrustProofItem[] }) {
  if (!items.length) return null;

  return (
    <div className="hero-trust-proof-strip" aria-label="Trust signals">
      {items.map((item, index) => (
        <div className="hero-trust-proof-item" key={item.id || `${item.title}-${index}`}>
          <span className="hero-trust-proof-icon" aria-hidden="true">
            <TokenIcon icon={item.icon} size={34} strokeWidth={1.8} />
          </span>
          <span className="hero-trust-proof-copy">
            <span className="hero-trust-proof-title">{item.title}</span>
            {item.text ? <span className="hero-trust-proof-text">{item.text}</span> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function HeroTrust({
  Link,
  image,
  eyebrow = t.defaults.eyebrow,
  title = t.defaults.title,
  titleHighlights = [...t.defaults.titleHighlights],
  titleComponent = "h1",
  lead = t.defaults.lead,
  completeTitle = t.defaults.completeTitle,
  completeText = t.defaults.completeText,
  reassuranceTitle = t.defaults.reassuranceTitle,
  reassuranceText = t.defaults.reassuranceText,
  primaryFeatures = [...t.defaults.primaryFeatures] as HeroTrustFeature[],
  secondaryFeatures = [...t.defaults.secondaryFeatures] as HeroTrustFeature[],
  mobileFeatures,
  primaryAction = t.defaults.primaryAction,
  secondaryAction = t.defaults.secondaryAction,
  supportNote = t.defaults.supportNote,
  proofItems = [...t.defaults.proofItems] as HeroTrustProofItem[],
  icons,
  colors,
  colours,
  accentColor,
  accentColour,
  backgroundColor,
  backgroundColour,
  panelColor,
  panelColour,
  textColor,
  textColour,
  surfaceColor,
  surfaceColour,
  surfaceTextColor,
  surfaceTextColour,
  maxWidth = t.sizes.maxWidth,
  id = "hero-trust",
  className = "",
  style,
  ariaLabel,
}: HeroTrustProps) {
  const imageSrc = getImageSrc(image);
  const HeadingTag = titleComponent;
  const safeId = sanitizeId(id);
  const titleId = `${safeId}-title`;
  const mergedColors = {
    ...t.colors,
    ...colours,
    ...colors,
  };
  const resolvedIcons: Required<HeroTrustIcons> = {
    ...t.icons,
    ...icons,
  };
  const resolvedTextColor = resolveTextColor(textColor || textColour);
  const resolvedSurfaceTextColor = resolveTextColor(surfaceTextColor || surfaceTextColour);
  const resolvedPrimaryAction = primaryAction || undefined;
  const resolvedSecondaryAction = secondaryAction || undefined;
  const resolvedSupportNote = supportNote || undefined;
  const resolvedMobileFeatures = mobileFeatures || [...primaryFeatures, ...secondaryFeatures];
  const accessibleLabel = ariaLabel || title;

  const sectionStyle: CssVarStyle = {
    "--hero-trust-bg": backgroundColor || backgroundColour || mergedColors.background,
    "--hero-trust-panel": panelColor || panelColour || mergedColors.panel,
    "--hero-trust-text": resolvedTextColor || mergedColors.text,
    "--hero-trust-text-soft": getSoftTextColor(resolvedTextColor) || mergedColors.textSoft,
    "--hero-trust-text-muted": mergedColors.textMuted,
    "--hero-trust-accent": accentColor || accentColour || mergedColors.accent,
    "--hero-trust-accent-dark": mergedColors.accentDark,
    "--hero-trust-accent-soft": mergedColors.accentSoft,
    "--hero-trust-accent-contrast": mergedColors.accentContrast,
    "--hero-trust-dark-surface": mergedColors.darkSurface,
    "--hero-trust-dark-surface-strong": mergedColors.darkSurfaceStrong,
    "--hero-trust-dark-border": mergedColors.darkBorder,
    "--hero-trust-light-surface": surfaceColor || surfaceColour || mergedColors.lightSurface,
    "--hero-trust-light-text": resolvedSurfaceTextColor || mergedColors.lightText,
    "--hero-trust-light-text-soft": mergedColors.lightTextSoft,
    "--hero-trust-light-border": mergedColors.lightBorder,
    "--hero-trust-proof-surface": mergedColors.proofSurface,
    "--hero-trust-proof-text": mergedColors.proofText,
    "--hero-trust-proof-text-soft": mergedColors.proofTextSoft,
    "--hero-trust-proof-divider": mergedColors.proofDivider,
    "--hero-trust-icon-ring": mergedColors.iconRing,
    "--hero-trust-light-icon-ring": mergedColors.lightIconRing,
    "--hero-trust-shadow": mergedColors.shadow,
    "--hero-trust-max-width": getCssSize(maxWidth),
    ...style,
  };

  return (
    <section
      className={`hero-trust ${className}`.trim()}
      data-component="hero-trust"
      data-has-image={imageSrc ? "true" : "false"}
      id={safeId}
      aria-labelledby={title ? titleId : undefined}
      aria-label={!title ? accessibleLabel : undefined}
      style={sectionStyle}
    >
      <style>{heroTrustCss}</style>

      <div className="hero-trust-shell">
        <div className="hero-trust-panel">
          {imageSrc ? (
            <img
              className="hero-trust-image"
              src={imageSrc}
              alt={image?.decorative === false ? getImageAlt(image, title) : ""}
              aria-hidden={image?.decorative === false ? undefined : "true"}
              width={image?.width}
              height={image?.height}
              loading={image?.loading || "eager"}
              decoding="async"
              style={image?.position ? { objectPosition: image.position } : undefined}
            />
          ) : null}

          <div className="hero-trust-content">
            <header className="hero-trust-header">
              {eyebrow ? (
                <div className="hero-trust-eyebrow">
                  <span className="hero-trust-eyebrow-icon" aria-hidden="true">
                    <TokenIcon icon={resolvedIcons.eyebrow} size={28} strokeWidth={2.2} />
                  </span>
                  <span className="hero-trust-eyebrow-text">{eyebrow}</span>
                </div>
              ) : null}

              {title ? (
                <HeadingTag className="hero-trust-title" id={titleId}>
                  {renderHighlightedTitle(title, titleHighlights)}
                </HeadingTag>
              ) : null}

              {lead ? <p className="hero-trust-lead">{lead}</p> : null}
            </header>

            <div className="hero-trust-feature-grid">
              <div className="hero-trust-feature-card hero-trust-feature-card-dark">
                {(completeTitle || completeText) ? (
                  <div className="hero-trust-complete-header">
                    <span className="hero-trust-card-badge" aria-hidden="true">
                      <TokenIcon icon={resolvedIcons.complete} size={31} strokeWidth={2} />
                    </span>
                    <span className="hero-trust-card-heading">
                      {completeTitle ? (
                        <span className="hero-trust-card-title">{completeTitle}</span>
                      ) : null}
                      {completeText ? <span className="hero-trust-card-text">{completeText}</span> : null}
                    </span>
                  </div>
                ) : null}
                <FeatureList features={primaryFeatures} icons={resolvedIcons} />
              </div>

              <div className="hero-trust-feature-card hero-trust-feature-card-light">
                {(reassuranceTitle || reassuranceText) ? (
                  <div className="hero-trust-reassurance-header">
                    <span className="hero-trust-feature-icon" aria-hidden="true">
                      <TokenIcon icon="leaf" size={31} strokeWidth={1.8} />
                    </span>
                    <span className="hero-trust-card-heading">
                      {reassuranceTitle ? (
                        <span className="hero-trust-card-title">{reassuranceTitle}</span>
                      ) : null}
                      {reassuranceText ? (
                        <span className="hero-trust-card-text">{reassuranceText}</span>
                      ) : null}
                    </span>
                  </div>
                ) : null}
                <FeatureList features={secondaryFeatures} icons={resolvedIcons} />
              </div>
            </div>

            <MobileFeatureList features={resolvedMobileFeatures} icons={resolvedIcons} />

            {(resolvedSupportNote || resolvedPrimaryAction || resolvedSecondaryAction) ? (
              <div className="hero-trust-actions-panel">
                {resolvedSupportNote ? (
                  <div className="hero-trust-support-note">
                    <span className="hero-trust-support-icon" aria-hidden="true">
                      <TokenIcon icon={resolvedSupportNote.icon} size={34} strokeWidth={2.4} />
                    </span>
                    <span className="hero-trust-support-copy">
                      <span className="hero-trust-support-title">{resolvedSupportNote.title}</span>
                      {resolvedSupportNote.text ? (
                        <span className="hero-trust-support-text">{resolvedSupportNote.text}</span>
                      ) : null}
                    </span>
                  </div>
                ) : null}

                {resolvedPrimaryAction ? (
                  <HeroAction action={resolvedPrimaryAction} variant="primary" Link={Link} />
                ) : null}
                {resolvedSecondaryAction ? (
                  <HeroAction action={resolvedSecondaryAction} variant="secondary" Link={Link} />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <ProofStrip items={proofItems} />
      </div>
    </section>
  );
}

export {
  heroTrustIconTokens,
  heroTrustTokens,
  type HeroTrustColorToken,
  type HeroTrustIconSlot,
  type HeroTrustIconToken,
};
